import React, { useMemo } from 'react';
import { StyledLogoBox, StyledFlex, StyledMaskedLogo } from './RegistryLogoGrid.styles';
import type { RegistryList } from '@apps/registry/common/state/RegistryProducts';
import type { RegistryFragment } from '@graphql/fragments/registry.graphql.generated';
import type { RegistryOpenHandler } from '../types';
import { Box, SpacingStack, TextV2, useTheme } from '@withjoy/joykit';
import { extractHostname } from '@shared/utils/extractHostname';
import ClampedText from '@apps/registry/common/components/ClampedText';
import { ReactComponent as JoyLogo } from '@assets/joy-logo.svg';
import { ReactComponent as LogoDefault } from '@assets/icons/reg-logo-default.svg';
import {
  isAmazonUri,
  isWilliamsSonomaUri,
  isAnthropologieUri,
  isBedBathAndBeyondUri,
  isCrateAndBarrelUri,
  isIkeaUri,
  isMacysUri,
  isPotteryBarnUri,
  isTargetUri,
  isWayfairUri,
  isWestElmUri,
} from '@apps/registry/common/util/storeHelpers';
import { ReactComponent as Amazon } from '@assets/icons/registrylogos/amazon.svg';
import { ReactComponent as WilliamsSonoma } from '@assets/icons/registrylogos/williamssonoma.svg';
import { ReactComponent as Anthropologie } from '@assets/icons/registrylogos/anthropologie.svg';
import { ReactComponent as BedBathAndBeyond } from '@assets/icons/registrylogos/bedbathandbeyond.svg';
import { ReactComponent as CrateAndBarrel } from '@assets/icons/registrylogos/crateandbarrel.svg';
import { ReactComponent as Ikea } from '@assets/icons/registrylogos/ikea.svg';
import { ReactComponent as Macys } from '@assets/icons/registrylogos/macys.svg';
import { ReactComponent as PotteryBarn } from '@assets/icons/registrylogos/potterybarn.svg';
import { ReactComponent as Target } from '@assets/icons/registrylogos/target.svg';
import { ReactComponent as Wayfair } from '@assets/icons/registrylogos/wayfair.svg';
import { ReactComponent as WestElm } from '@assets/icons/registrylogos/westelm.svg';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';

import { useRegistryGuestTranslations } from '../../GuestRegistry.i18n';
import { pxToRem } from '@withjoy/joykit/theme';
import type { CookedProduct } from '@apps/registry/common/selectors/ProductListSelector';

type RetailerLogoConfig = Readonly<{
  matcher: (url: Maybe<string>) => boolean;
  component: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}>;

const RETAILER_LOGOS: readonly RetailerLogoConfig[] = [
  { matcher: isAmazonUri, component: Amazon },
  { matcher: isWilliamsSonomaUri, component: WilliamsSonoma },
  { matcher: isAnthropologieUri, component: Anthropologie },
  { matcher: isBedBathAndBeyondUri, component: BedBathAndBeyond },
  { matcher: isCrateAndBarrelUri, component: CrateAndBarrel },
  { matcher: isIkeaUri, component: Ikea },
  { matcher: isMacysUri, component: Macys },
  { matcher: isPotteryBarnUri, component: PotteryBarn },
  { matcher: isTargetUri, component: Target },
  { matcher: isWayfairUri, component: Wayfair },
  { matcher: isWestElmUri, component: WestElm },
] as const;

const LogoRenderer: React.FC<{ url: Maybe<string>; imageUrl?: string | null; text: string; linkColor: string }> = ({ url, imageUrl, text, linkColor }) => {
  const matchedRetailer = useMemo(() => RETAILER_LOGOS.find(config => config.matcher(url)), [url]);

  if (imageUrl) {
    const LogoComponent = matchedRetailer?.component;
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
        color={linkColor}
      >
        {LogoComponent ? (
          <LogoComponent
            width="100%"
            height="100%"
            style={{ maxWidth: '80px', maxHeight: '60px' }}
          />
        ) : (
          <StyledMaskedLogo
            style={{ ['--registry-logo-image' as string]: `url(${JSON.stringify(imageUrl)})` }}
            role="img"
            aria-label={text}
          />
        )}
      </Box>
    );
  }

  return (
    <SpacingStack
      spacing={3}
      alignItems="center"
    >
      <LogoDefault />
      <ClampedText
        component="p"
        typographyVariant="button1"
        textAlign="center"
        clampAt={2}
      >
        {text}
      </ClampedText>
    </SpacingStack>
  );
};

interface Props
  extends Readonly<{
    logosToShow: RegistryList;
    handleClickRegistry?: RegistryOpenHandler;
    isCustomProduct?: boolean;
    onClickCustomItem: () => void;
    product?: CookedProduct;
  }> {}

export const RegistryLogoGrid: React.FC<Props> = ({ logosToShow, handleClickRegistry, isCustomProduct, product, onClickCustomItem }) => {
  const { getGiveGiftDialogTranslations } = useRegistryGuestTranslations();
  const { alsoRegisteredAt } = getGiveGiftDialogTranslations();

  const { space, colors } = useTheme();
  const { accentColor } = useColorPaletteProvider();
  const linkColor = accentColor || colors.linkText;

  return (
    <Box paddingTop={pxToRem(128)}>
      <TextV2
        typographyVariant="hed4"
        marginBottom={space[6]}
        textAlign="center"
      >
        {alsoRegisteredAt()}
      </TextV2>
      <StyledFlex
        justifyContent="center"
        maxWidth={['592px', '687px', '879px', '944px', '1101px', null, '1264px', '1504px', '1745px']}
      >
        {logosToShow?.map((logo: RegistryFragment) => {
          const text = logo.nameV0204 || extractHostname(logo.urlV0204!);
          const syncStatus = logo.wantSync === false ? 'notwanted' : logo.wantSync === true ? 'wanted' : 'default';
          return (
            <Box key={logo.id}>
              <StyledLogoBox
                position="relative"
                onClick={() => {
                  if (logo.lastSyncAt && syncStatus !== 'notwanted') {
                    onClickCustomItem();
                  } else if (handleClickRegistry && logo.id && logo.urlV0204) {
                    handleClickRegistry({ id: logo.id, url: logo.urlV0204, product });
                  }
                }}
              >
                <LogoRenderer
                  url={logo.urlV0204}
                  imageUrl={logo.imageUrlV0204}
                  text={text}
                  linkColor={linkColor}
                />
              </StyledLogoBox>
            </Box>
          );
        })}
        {isCustomProduct && (
          <Box>
            <StyledLogoBox onClick={onClickCustomItem}>
              <JoyLogo />
            </StyledLogoBox>
          </Box>
        )}
      </StyledFlex>
    </Box>
  );
};

RegistryLogoGrid.displayName = 'RegistryLogoGrid';
